import { startTransition, useCallback, useEffect, useState } from 'react';
import { NWClient } from '../../../client/NWClient';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import { useErrorMessage } from '../../../common/hooks/useErrorMessage';

export const useDocumentContent = (docId: number, fileType: string | null) => {
  const { getErrorMessage } = useErrorMessage();
  const [params] = useSearchParams();
  const generateTemplateParam = params.get('generateTemplate');
  const editDocumentParam = params.get('editDocument');
  // Kept as runtime null: an empty string would render <iframe src=""> and reload the page.
  const [fileBlobURL, setFileBlobURL] = useState<string>(null as unknown as string);
  const [fileLoading, setFileLoading] = useState<boolean>(false);
  const [documentHTML, setDocumentHTML] = useState<string>('Something in the way');
  const [pdfBlobMode, setPDFBlobMode] = useState<boolean>(
    !generateTemplateParam && !editDocumentParam
  );
  const creatingDocumentBlob = useCallback(
    (open?: boolean) => {
      startTransition(() => setFileLoading(true));
      NWClient.getDocumentBlob(docId)
        .then((res: string) => {
          setFileBlobURL(res);
          if (open) {
            window.open(res, '_blank');
          }
        })
        .catch((error) => {
          toast.error(getErrorMessage(error));
        })
        .finally(() => setFileLoading(false));
    },
    [docId, getErrorMessage]
  );

  const getImageBlob = useCallback(() => {
    startTransition(() => setFileLoading(true));
    NWClient.getDocumentBlob(docId)
      .then((res: string) => {
        setFileBlobURL(res);
      })
      .catch((error) => {
        toast.error(getErrorMessage(error));
      })
      .finally(() => setFileLoading(false));
  }, [docId, getErrorMessage]);

  const blobHandling = useCallback(() => {
    if (fileType === 'pdf' && docId) {
      creatingDocumentBlob();
    }

    if (fileType && ['jpg', 'jpeg', 'png', 'heic'].includes(fileType.toLowerCase()) && docId) {
      getImageBlob();
    }
  }, [docId, fileType, creatingDocumentBlob, getImageBlob]);

  useEffect(() => {
    blobHandling();
  }, [blobHandling]);

  useEffect(() => {
    return () => {
      URL.revokeObjectURL(fileBlobURL);
    };
  }, [fileBlobURL]);

  return {
    fileBlobURL,
    fileLoading,
    creatingDocumentBlob,
    setPDFBlobMode,
    pdfBlobMode,
    documentHTML,
    setDocumentHTML,
  };
};
